import { useEffect, useRef } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { TopBar } from '../../components/editor/TopBar';
import { LeftPanel } from '../../components/editor/LeftPanel/LeftPanel';
import { EditorCanvas, type EditorCanvasHandle } from '../../components/editor/canvas/EditorCanvas';
import { CropModeToolbar } from '../../components/editor/CropModeToolbar';
import { PropertiesPanel } from '../../components/editor/PropertiesPanel/PropertiesPanel';
import { PageStrip } from '../../components/editor/PageStrip/PageStrip';
import { useEditorShortcuts } from '../../components/editor/useEditorShortcuts';
import { useProjectStore } from '../../state/projectStore';
import { Spinner } from '../../components/ui/Spinner';
import { EmptyState } from '../../components/ui/EmptyState';
import { Button } from '../../components/ui/Button';
import { t } from '../../i18n';
import './editor-page.css';

export default function EditorPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const status = useProjectStore((s) => s.status);
  const loadError = useProjectStore((s) => s.loadError);
  const loadProject = useProjectStore((s) => s.loadProject);
  const reset = useProjectStore((s) => s.reset);
  const project = useProjectStore((s) => s.project);
  const setView = useProjectStore((s) => s.setView);
  const setSelection = useProjectStore((s) => s.setSelection);
  const canvasApiRef = useRef<EditorCanvasHandle>(null);

  useEditorShortcuts();

  useEffect(() => {
    if (projectId) void loadProject(projectId);
    return () => reset();
  }, [projectId, loadProject, reset]);

  // Deep-link support from the export/preflight screen: /editor/:id?page=..&element=..
  useEffect(() => {
    if (status !== 'ready' || !project) return;
    const pageId = searchParams.get('page');
    const panel = searchParams.get('panel') as 'front' | 'back' | 'spine' | null;
    const elementId = searchParams.get('element');
    if (!pageId && !panel) return;

    if (panel) {
      setView({ mode: 'cover' });
      if (elementId) setSelection({ target: { scope: 'cover', panel }, elementIds: [elementId] });
    } else if (pageId) {
      const page = project.pages.find((p) => p.id === pageId);
      if (page) {
        setView({ mode: 'spread', spreadIndex: Math.floor(page.order / 2) });
        if (elementId) setSelection({ target: { scope: 'page', pageId }, elementIds: [elementId] });
      }
    }
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, project?.id]);

  if (status === 'loading' || status === 'idle') {
    return (
      <div className="editor-loading">
        <Spinner size={32} />
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="editor-loading">
        <EmptyState
          title={loadError ?? t('errors.projectNotFound')}
          action={
            <Button asChild variant="primary">
              <Link to="/projecten">{t('nav.projects')}</Link>
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="editor-page">
      <TopBar onFitZoom={() => canvasApiRef.current?.fitToContainer()} />
      <div className="editor-body">
        <LeftPanel />
        <div className="editor-canvas-area">
          <EditorCanvas ref={canvasApiRef} />
          <CropModeToolbar />
        </div>
        <PropertiesPanel />
      </div>
      <PageStrip />
    </div>
  );
}
